// src/components/Seller/SellerProducts.js
import React, { useEffect, useState } from "react";
import "../ProductManagement/Products.css";
import config from "../../config";
import SellerCreateProductForm from "./SellerCreateProductForm";
import SellerUpdateProduct from "./SellerUpdateProduct";

export default function SellerProducts({ sellerId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const token = localStorage.getItem("seller_id_token");

  const fetchSellerProducts = async () => {
    if (!sellerId) return;
    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `${config.baseUrl}/api/seller/products/${sellerId}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            Accept: "application/json",
          },
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load products");
      }

      const data = await res.json();
      setProducts(data || []);
    } catch (err) {
      console.error("Seller products fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellerProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const toggleExpanded = (productId) => {
    const copy = new Set(expandedProducts);
    if (copy.has(productId)) copy.delete(productId);
    else copy.add(productId);
    setExpandedProducts(copy);
  };

  const filteredProducts = products.filter((product) => {
    const name = product.productName || "";
    const desc = product.productDescription || "";
    const id = product.productId ? String(product.productId) : "";
    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      id.includes(searchTerm);

    const categoryName =
      product.category?.name || product.categoryName || "";
    const matchesCategory =
      !categoryFilter || categoryName === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <div className="loading">Loading your products...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="products-section">
      <div className="section-header">
        <div className="header-content">
          <h2>My Products</h2>
          <p>Create and manage products listed under your seller account</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={() => setShowCreate(true)}
          >
            <span className="btn-icon">➕</span>
            Add Product
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search by name, description or product ID..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          <option value="Boys">Boys</option>
          <option value="Girls">Girls</option>
          <option value="Ladies">Ladies</option>
          <option value="Men">Men</option>
          <option value="Women">Women</option>
        </select>
      </div>

      <div className="products-grid">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <div className="no-products-icon">🧺</div>
            <h3>No products found</h3>
            <p>Start by adding your first product</p>
            <button
              className="btn-primary"
              onClick={() => setShowCreate(true)}
            >
              Add Product
            </button>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <SellerProductCard
              key={product.productId}
              product={product}
              isExpanded={expandedProducts.has(product.productId)}
              onToggleExpanded={() => toggleExpanded(product.productId)}
              onEdit={() => {
                setSelectedProduct(product);
                setShowUpdate(true);
              }}
            />
          ))
        )}
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {showCreate && (
        <SellerCreateProductForm
          sellerId={sellerId}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            setSuccessMessage("Product created successfully!");
            fetchSellerProducts();
            setTimeout(() => setSuccessMessage(""), 4000);
          }}
        />
      )}

      {showUpdate && selectedProduct && (
        <SellerUpdateProduct
          sellerId={sellerId}
          product={selectedProduct}
          onClose={() => {
            setShowUpdate(false);
            setSelectedProduct(null);
          }}
          onProductUpdated={() => {
            setShowUpdate(false);
            setSelectedProduct(null);
            setSuccessMessage("Product updated successfully!");
            fetchSellerProducts();
            setTimeout(() => setSuccessMessage(""), 4000);
          }}
        />
      )}
    </div>
  );
}

function SellerProductCard({ product, isExpanded, onToggleExpanded, onEdit }) {
  const mainImage =
    product.images?.find((img) => img.image_type === "MAIN")?.image_url ||
    product.images?.[0]?.image_url ||
    "https://via.placeholder.com/200x200?text=No+Image";

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const prices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((p) => p && !isNaN(p)) || [];

  const minPrice = prices.length ? Math.min(...prices) : 0;
  const maxPrice = prices.length ? Math.max(...prices) : 0;

  const formatPrice = (p) => (p ? `₹${p.toFixed(2)}` : "N/A");
  const priceDisplay =
    prices.length === 0
      ? "N/A"
      : minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
        />
        <div className="product-status">
          <span
            className={`status-badge ${
              product.active ? "active" : "inactive"
            }`}
          >
            {product.active ? "Active" : "Under Review"}
          </span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name ||
                product.categoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Color:</span>
            <span className="meta-value">{product.color || "N/A"}</span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.variants?.length || 0}
            </span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.variants?.length > 0 && (
          <div className="variants-section">
            <h4>Variants & Pricing</h4>
            <div className="variants-grid">
              {product.variants.map((variant) => (
                <div
                  key={variant.variantId || variant.id || variant.sku}
                  className="variant-card"
                >
                  <div className="variant-size">
                    {variant.size?.name || variant.sizeName || "N/A"}
                  </div>
                  <div className="variant-sku">{variant.sku}</div>
                  <div className="variant-prices">
                    {variant.originalPrice && (
                      <span className="original-price">
                        ₹{Number(variant.originalPrice).toFixed(2)}
                      </span>
                    )}
                    {(variant.offerPrice || variant.originalPrice) && (
                      <span className="offer-price">
                        ₹{Number(
                          variant.offerPrice || variant.originalPrice
                        ).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div
                    className={`variant-stock ${
                      (variant.stockQuantity || 0) < 5
                        ? "low-stock"
                        : ""
                    }`}
                  >
                    Stock: {variant.stockQuantity || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && product.images?.length > 0 && (
          <div className="images-section">
            <h4>Product Images</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img src={img.image_url} alt={`Product ${idx}`} />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || "IMAGE"}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ View Details"}
          </button>
          <button className="btn-manage" onClick={onEdit}>
            ✏️ Edit
          </button>
        </div>
      </div>
    </div>
  );
}
